/**
 * Configuration Management Module
 * 
 * Handles loading and accessing application configuration from window.CONFIG
 * or environment variables
 * 
 * Requirements: 17.1, 17.4
 */

import type { CognitoConfig } from './cognito';

export interface AppConfig extends CognitoConfig {
  environment: 'development' | 'production';
  features: {
    mcpServers: boolean;
    sipIntegration: boolean;
    conversationHistory: boolean;
  };
  devMode: boolean;
  bypassAuth: boolean;
  dialPlanApiUrl?: string;
  sipServerApiUrl?: string;
  mcpServerApiUrl?: string;
  historyApiUrl?: string;
}

export interface ConfigValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

class ConfigManager {
  private config: AppConfig | null = null;
  private validationResult: ConfigValidationResult | null = null;

  /**
   * Load configuration from window.CONFIG or environment variables
   * Validates required configuration and handles missing values
   */
  loadConfig(): AppConfig {
    if (this.config) {
      return this.config;
    }

    // Try to load from window.CONFIG first (production)
    if (window.CONFIG) {
      this.config = {
        ...window.CONFIG,
        environment: (import.meta.env.VITE_ENVIRONMENT as 'development' | 'production') || 'production',
        features: {
          mcpServers: window.CONFIG.features?.mcpServers ?? true,
          sipIntegration: window.CONFIG.features?.sipIntegration ?? true,
          conversationHistory: window.CONFIG.features?.conversationHistory ?? true,
        },
        devMode: import.meta.env.VITE_DEV_MODE === 'true' || false,
        bypassAuth: import.meta.env.VITE_BYPASS_AUTH === 'true' || false,
      };
      
      // Validate configuration after loading
      this.validationResult = this.performValidation(this.config);
      
      return this.config;
    }

    // Fall back to environment variables (development)
    const cognitoUserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID as string;
    const cognitoAppClientId = import.meta.env.VITE_COGNITO_APP_CLIENT_ID as string;
    const cognitoDomain = import.meta.env.VITE_COGNITO_DOMAIN as string;
    const backendEndpoint = import.meta.env.VITE_BACKEND_URL as string || 'http://localhost:8000';
    const backendUrl = import.meta.env.VITE_BACKEND_URL as string || 'http://localhost:8000';
    const appUrl = import.meta.env.VITE_APP_URL as string || window.location.origin;

    this.config = {
      cognitoUserPoolId,
      cognitoAppClientId,
      cognitoDomain,
      backendEndpoint,
      backendUrl,
      appUrl,
      apiGatewayUrl: import.meta.env.VITE_API_GATEWAY_URL as string,
      phoneInventoryApiUrl: import.meta.env.VITE_PHONE_INVENTORY_API_URL as string,
      environment: (import.meta.env.VITE_ENVIRONMENT as 'development' | 'production') || 'development',
      features: {
        mcpServers: import.meta.env.VITE_ENABLE_MCP_SERVERS !== 'false',
        sipIntegration: import.meta.env.VITE_ENABLE_SIP_INTEGRATION !== 'false',
        conversationHistory: import.meta.env.VITE_ENABLE_CONVERSATION_HISTORY !== 'false',
      },
      devMode: import.meta.env.VITE_DEV_MODE === 'true' || import.meta.env.MODE === 'development',
      bypassAuth: import.meta.env.VITE_BYPASS_AUTH === 'true',
    };

    // Validate configuration after loading
    this.validationResult = this.performValidation(this.config);

    return this.config;
  }

  /**
   * Perform validation on the configuration
   */
  private performValidation(config: AppConfig): ConfigValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];
    const isDev = config.devMode || config.environment === 'development';

    // In development mode, authentication fields are optional if auth is bypassed
    const authRequired = !isDev || !config.bypassAuth;

    // Required fields for authentication (only if auth is not bypassed in dev)
    if (authRequired) {
      if (!config.cognitoUserPoolId) {
        errors.push('Missing required configuration: Cognito User Pool ID (cognitoUserPoolId)');
      }

      if (!config.cognitoAppClientId) {
        errors.push('Missing required configuration: Cognito App Client ID (cognitoAppClientId)');
      }

      if (!config.cognitoDomain) {
        errors.push('Missing required configuration: Cognito Domain (cognitoDomain)');
      }
    } else {
      // In dev mode with bypassed auth, these are just warnings
      if (!config.cognitoUserPoolId) {
        warnings.push('Missing optional configuration: Cognito User Pool ID (cognitoUserPoolId) - not required in dev mode with bypassed auth');
      }

      if (!config.cognitoAppClientId) {
        warnings.push('Missing optional configuration: Cognito App Client ID (cognitoAppClientId) - not required in dev mode with bypassed auth');
      }

      if (!config.cognitoDomain) {
        warnings.push('Missing optional configuration: Cognito Domain (cognitoDomain) - not required in dev mode with bypassed auth');
      }
    }

    // Backend URL is always required (defaults to localhost in dev)
    if (!config.backendEndpoint && !config.backendUrl) {
      if (isDev) {
        warnings.push('Missing backend URL - will default to http://localhost:8000');
      } else {
        errors.push('Missing required configuration: Backend URL (backendEndpoint or backendUrl)');
      }
    }

    // App URL defaults to window.location.origin in dev
    if (!config.appUrl) {
      if (isDev) {
        warnings.push('Missing app URL - will default to current origin');
      } else {
        errors.push('Missing required configuration: Application URL (appUrl)');
      }
    }

    // Optional fields - warnings only
    if (!config.apiGatewayUrl) {
      warnings.push('Missing optional configuration: API Gateway URL (apiGatewayUrl)');
    }

    if (!config.phoneInventoryApiUrl) {
      warnings.push('Missing optional configuration: Phone Inventory API URL (phoneInventoryApiUrl)');
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Get the current configuration
   */
  getConfig(): AppConfig {
    if (!this.config) {
      return this.loadConfig();
    }
    return this.config;
  }

  /**
   * Check if running in development mode
   * Requirements: 17.2
   */
  isDevelopment(): boolean {
    const config = this.getConfig();
    return config.environment === 'development' || config.devMode;
  }

  /**
   * Check if running in production mode
   * Requirements: 17.3
   */
  isProduction(): boolean {
    return !this.isDevelopment();
  }

  /**
   * Check if authentication should be bypassed
   */
  shouldBypassAuth(): boolean {
    const config = this.getConfig();
    return config.bypassAuth && this.isDevelopment();
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(feature: keyof AppConfig['features']): boolean {
    const config = this.getConfig();
    return config.features[feature];
  }

  /**
   * Get the appropriate backend endpoint based on environment
   * Development: localhost endpoints
   * Production: CloudFront URLs
   * Requirements: 17.2, 17.3
   */
  getBackendEndpoint(): string {
    const config = this.getConfig();
    
    // Use configured URLs if available
    if (config.backendEndpoint) {
      return config.backendEndpoint;
    }
    
    if (config.backendUrl) {
      return config.backendUrl;
    }
    
    // Fall back to localhost in development mode
    if (this.isDevelopment()) {
      return 'http://localhost:8000';
    }
    
    // Production fallback - use current origin
    console.warn('No backend URL configured, using current origin');
    return window.location.origin;
  }

  /**
   * Get the WebSocket URL based on environment
   */
  getWebSocketUrl(): string {
    const backendUrl = this.getBackendEndpoint();
    
    // Convert HTTP(S) to WS(S)
    if (backendUrl.startsWith('https://')) {
      return backendUrl.replace('https://', 'wss://');
    } else if (backendUrl.startsWith('http://')) {
      return backendUrl.replace('http://', 'ws://');
    }
    
    return backendUrl;
  }

  /**
   * Validate that required configuration is present
   * Returns cached validation result if available
   */
  validateConfig(): ConfigValidationResult {
    if (this.validationResult) {
      return this.validationResult;
    }

    const config = this.getConfig();
    this.validationResult = this.performValidation(config);
    return this.validationResult;
  }

  /**
   * Get a specific configuration value
   */
  getConfigValue<K extends keyof AppConfig>(key: K): AppConfig[K] {
    const config = this.getConfig();
    return config[key];
  }

  /**
   * Update a configuration value (for non-sensitive values only)
   * This is used by the Settings view
   */
  updateConfigValue<K extends keyof AppConfig>(key: K, value: AppConfig[K]): void {
    // Only allow updating certain non-sensitive values
    const allowedKeys: (keyof AppConfig)[] = ['features', 'devMode'];
    
    if (!allowedKeys.includes(key)) {
      throw new Error(`Cannot update configuration value: ${String(key)} is read-only`);
    }

    if (this.config) {
      this.config[key] = value;
      // Re-validate after update
      this.validationResult = this.performValidation(this.config);
    }
  }

  /**
   * Reset configuration (useful for testing)
   */
  resetConfig(): void {
    this.config = null;
    this.validationResult = null;
  }
}

// Export singleton instance
export const configManager = new ConfigManager();

// Export convenience functions
export const getConfig = () => configManager.getConfig();
export const isDevelopment = () => configManager.isDevelopment();
export const isProduction = () => configManager.isProduction();
export const shouldBypassAuth = () => configManager.shouldBypassAuth();
export const isFeatureEnabled = (feature: keyof AppConfig['features']) =>
  configManager.isFeatureEnabled(feature);
export const validateConfig = () => configManager.validateConfig();
export const getConfigValue = <K extends keyof AppConfig>(key: K) => 
  configManager.getConfigValue(key);
export const updateConfigValue = <K extends keyof AppConfig>(key: K, value: AppConfig[K]) =>
  configManager.updateConfigValue(key, value);
export const getBackendEndpoint = () => configManager.getBackendEndpoint();
export const getWebSocketUrl = () => configManager.getWebSocketUrl();
